import {Tr} from "@/i18n/i18n";
import {Keys} from "@/constants/key";
import TextUtils from "@/utils/text-utils";
import SpriteUtils from "@/utils/sprite-utils";
import {ForceBasePanel} from "@/scenes.ui/force-base-panel";
import {SquareButton} from "@/scenes.ui/square-button";
import {CreatureUtils} from "@/utils/creature-utils";
import {WarriorClass} from "@/store/level/types";
import {Fonts} from "@/constants/font";
import CU from "@/utils/c-u";
import PlayerStore from "@/store/player";
import {CardType} from "@/store/card/types";
import {CardPanel} from "@/scenes.ui/card-panel";
import CardStore from "@/store/card";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;
import ListenerManager from "@/managers/listener-manager";

export class EvolutionPanel extends ForceBasePanel {
    private creatureId: number;
    private cancelButton: SquareButton;
    private evolveButton: SquareButton;

    constructor(scene: Scene, canvas: HTMLCanvasElement, private selectedForce: WarriorClass, private targetForce: WarriorClass, private cancelCallback: () => void, private evolveCallback: () => void, slotX: number, slotY: number, container: Container, creatureId: number) {
        super(scene, canvas, slotX, slotY, container);
        this.creatureId = creatureId;
        const titleText = TextUtils.addTextByFamily(this.scene, Tr("evolution"), 0, -278, "#ffffff", 16, "bold", 420, Fonts.ARIAL_MT).setOrigin(0.5, 0.5);
        this.add(titleText);

        const currentForceName = CreatureUtils.getClassName(this.selectedForce);
        const cardState = PlayerStore.GET_CARDS_STATE();
        const activeCardType: CardType | null = cardState ? cardState.activeCard : null;

        const targetForceName = activeCardType ? CreatureUtils.getClassName(EvolutionPanel.getWarriorClassByCardType(activeCardType) || this.targetForce) : CreatureUtils.getClassName(this.targetForce);

        const evolutionTextStr = Tr("evolution_text").replace("[currentForce]", currentForceName).replace("[targetForce]", targetForceName);
        const evolutionText = TextUtils.addTextByFamily(this.scene, evolutionTextStr, 0, -180 * CU.S, "#ffdd00", 18, "normal", 300, Fonts.MYRIADPRO_BOLD).setOrigin(0.5, 0.5).setAlign("center");
        this.add(evolutionText);


        const panelCenterY = 20;

        const filteredIcons = this.getFilteredForceIconAndAtlas(PlayerStore.GET_CARDS_STATE()?.activeCard || null);

        const leftIcon = SpriteUtils.addSprite(this.scene, -87, panelCenterY, CreatureUtils.getForceAtlasLarge(this.selectedForce), CreatureUtils.getLargeForceIcon(this.selectedForce, true), 0.8);
        leftIcon.setOrigin(0.5, 0.5);
        this.add(leftIcon);

        const arrow = SpriteUtils.addSprite(this.scene, 0, panelCenterY, Keys.ATLAS_PROFILE, Keys.UI_ART_ARR, 1);
        arrow.setOrigin(0.5, 0.5);
        this.add(arrow);

        const rightIcon = SpriteUtils.addSprite(this.scene, 87, panelCenterY, filteredIcons.atlas, filteredIcons.icon, 0.8);
        rightIcon.setOrigin(0.5, 0.5);
        this.add(rightIcon);

        this.cancelButton = new SquareButton(this.scene, -70, 192, -15 * CU.S, Keys.UI_ART_BROWN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_DECLINE_CROSS, Tr('otmena'), false, 90 * CU.S, 90 * CU.S);

        this.cancelButton.setScale(0.9, 0.9);
        this.cancelButton.setCallback(() => {
            this.close();
            this.cancelCallback();
        });
        this.add(this.cancelButton);

        const activeCard = PlayerStore.GET_CARDS_STATE()?.activeCard || null;
        const cardIconKey = activeCard ? CardPanel.getCardSmallImage(activeCard) : Keys.UI_ARROW_RIGHT;

        this.evolveButton = new SquareButton(this.scene, 60, 192, CU.S, Keys.UI_ART_GREEN_BUTTON, undefined, cardIconKey, Tr('evolution'), false, 90 * CU.S, 110 * CU.S);
        this.evolveButton.setScale(0.9, 0.9);

        this.evolveButton.setCallback(() => {
            const creatureId = this.getCreatureId();
            const activeCardType = PlayerStore.GET_CARDS_STATE()?.activeCard || null;

            let targetWarriorClass = EvolutionPanel.getWarriorClassByCardType(activeCardType) || this.targetForce;
            if (PlayerStore.GET_CARDS_STATE()?.activeCard === CardType.EVOLUTION_UNIVERSAL) {
                targetWarriorClass = this.targetForce;
            }
            if (!targetWarriorClass) {
                return;
            }

            CardStore.evolve(creatureId, this.selectedForce, targetWarriorClass).then((success) => {
                if (success) {
                    ListenerManager.callEvolutionCallback();
                }
            }).catch((error: Error) => {
                console.error(error);
                this.close();
                this.cancelCallback();
            });
            this.close();
        });

        this.add(this.evolveButton);

    }

    public static getWarriorClassByCardType(cardType: CardType | null): WarriorClass | null {
        if (!cardType) return null;

        const mapping: Partial<Record<CardType, WarriorClass>> = {
            [CardType.EVOLUTION_ACE]: WarriorClass.AS,
            [CardType.EVOLUTION_LUCK]: WarriorClass.LUCK,
            [CardType.EVOLUTION_CHAOS]: WarriorClass.CHAOS,
            [CardType.EVOLUTION_DEATH]: WarriorClass.DEATH,
            [CardType.EVOLUTION_DRYAD]: WarriorClass.DRYAD,
            [CardType.EVOLUTION_FACELESS]: WarriorClass.FACELESS,
            [CardType.EVOLUTION_GORGON]: WarriorClass.GORGON,
            [CardType.EVOLUTION_HUMAN]: WarriorClass.HUMAN,
            [CardType.EVOLUTION_KOSCHEY]: WarriorClass.KOSCHEY,
            [CardType.EVOLUTION_PHOENIX]: WarriorClass.PHOENIX,
            [CardType.EVOLUTION_SHADOW]: WarriorClass.SHADOW,
            [CardType.EVOLUTION_SPHINX]: WarriorClass.SPHINX,
            [CardType.EVOLUTION_WITCH]: WarriorClass.WITCH,
            [CardType.EVOLUTION_DRUID]: WarriorClass.DRUID,
            [CardType.EVOLUTION_RIDER]: WarriorClass.RIDER,
            [CardType.EVOLUTION_SUKKUB]: WarriorClass.SUKKUB,
            [CardType.EVOLUTION_INKUB]: WarriorClass.INKUB,
            [CardType.EVOLUTION_NUN]: WarriorClass.NUN,
        };

        return mapping[cardType] ?? null;
    }

    private getFilteredForceIconAndAtlas(cardType: CardType | null): { atlas: string, icon: string } {
        if (!cardType) {
            return {
                atlas: CreatureUtils.getForceAtlasLarge(this.targetForce),
                icon: CreatureUtils.getLargeForceIcon(this.targetForce, true),
            };
        }

        const matchingForces: Partial<Record<CardType, WarriorClass>> = {
            [CardType.EVOLUTION_ACE]: WarriorClass.AS,
            [CardType.EVOLUTION_LUCK]: WarriorClass.LUCK,
            [CardType.EVOLUTION_CHAOS]: WarriorClass.CHAOS,
            [CardType.EVOLUTION_DEATH]: WarriorClass.DEATH,
            [CardType.EVOLUTION_DRYAD]: WarriorClass.DRYAD,
            [CardType.EVOLUTION_FACELESS]: WarriorClass.FACELESS,
            [CardType.EVOLUTION_GORGON]: WarriorClass.GORGON,
            [CardType.EVOLUTION_HUMAN]: WarriorClass.HUMAN,
            [CardType.EVOLUTION_KOSCHEY]: WarriorClass.KOSCHEY,
            [CardType.EVOLUTION_PHOENIX]: WarriorClass.PHOENIX,
            [CardType.EVOLUTION_SHADOW]: WarriorClass.SHADOW,
            [CardType.EVOLUTION_SPHINX]: WarriorClass.SPHINX,
            [CardType.EVOLUTION_WITCH]: WarriorClass.WITCH,
            [CardType.EVOLUTION_DRUID]: WarriorClass.DRUID,
            [CardType.EVOLUTION_RIDER]: WarriorClass.RIDER,
            [CardType.EVOLUTION_SUKKUB]: WarriorClass.SUKKUB,
            [CardType.EVOLUTION_INKUB]: WarriorClass.INKUB,
            [CardType.EVOLUTION_NUN]: WarriorClass.NUN,
        };

        const warriorClass = matchingForces[cardType] || this.targetForce;

        return {
            atlas: CreatureUtils.getForceAtlasLarge(warriorClass),
            icon: CreatureUtils.getLargeForceIcon(warriorClass, true),
        };
    }

    private getCreatureId(): number {
        return this.creatureId;
    }
}